"use client";

import { TURNSTILE_CHAT_ACTION, TURNSTILE_SCRIPT_SRC, turnstileSiteKeyForHost } from "./turnstile";

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_TIMEOUT_MS = 10_000;
const TOKEN_TIMEOUT_MS = 15_000;
/** A person who must click the checkbox gets time; a silent check never needs it. */
const INTERACTIVE_TIMEOUT_MS = 180_000;
let scriptLoad: Promise<TurnstileApi | null> | null = null;

/** One script per page; null when blocked (ad blocker, offline). */
function loadTurnstile(): Promise<TurnstileApi | null> {
  if (window.turnstile !== undefined) return Promise.resolve(window.turnstile);
  if (scriptLoad !== null) return scriptLoad;
  scriptLoad = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    const timer = window.setTimeout(() => resolve(window.turnstile ?? null), SCRIPT_TIMEOUT_MS);
    script.onload = () => {
      window.clearTimeout(timer);
      resolve(window.turnstile ?? null);
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      scriptLoad = null;
      resolve(null);
    };
    document.head.appendChild(script);
  });
  return scriptLoad;
}

/**
 * Where a checkbox may appear. Mode „Managed“ + `interaction-only`: almost always nothing
 * shows; a browser Cloudflare doubts gets a checkbox instead of a dead end. Inside `host`
 * (the chat, above the composer) when given, else bottom centre of the page.
 */
function challengeSlot(host: HTMLElement | undefined): HTMLElement {
  const slot = document.createElement("div");
  if (host !== undefined) {
    host.appendChild(slot);
    return slot;
  }
  slot.style.position = "fixed";
  slot.style.left = "50%";
  slot.style.bottom = "96px";
  slot.style.transform = "translateX(-50%)";
  slot.style.zIndex = "2147483000";
  document.body.appendChild(slot);
  return slot;
}

/**
 * Fresh single-use token; null = no token (script blocked, check failed, aborted, not configured).
 * `onInteractive(true)` while Cloudflare waits for the checkbox click.
 */
export async function fetchTurnstileToken(
  host?: HTMLElement,
  signal?: AbortSignal,
  onInteractive?: (waiting: boolean) => void,
): Promise<string | null> {
  const siteKey = turnstileSiteKeyForHost(window.location.hostname);
  if (!siteKey) return null;
  const turnstile = await loadTurnstile();
  if (turnstile === null || signal?.aborted === true) return null;

  const slot = challengeSlot(host);
  return await new Promise<string | null>((resolve) => {
    let widgetId: string | null = null;
    let settled = false;
    let waiting = false;
    let timer = window.setTimeout(() => finish(null), TOKEN_TIMEOUT_MS);
    function setWaiting(next: boolean): void {
      if (waiting === next) return;
      waiting = next;
      onInteractive?.(next);
    }
    function finish(token: string | null): void {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      setWaiting(false);
      if (widgetId !== null) {
        try {
          turnstile?.remove(widgetId);
        } catch {
          // Widget already gone.
        }
      }
      slot.remove();
      resolve(token);
    }
    // Chat closed or remounted: the widget leaves with it.
    signal?.addEventListener("abort", () => finish(null), { once: true });
    try {
      widgetId = turnstile.render(slot, {
        sitekey: siteKey,
        action: TURNSTILE_CHAT_ACTION,
        appearance: "interaction-only",
        // We remove the widget on error; Cloudflare's own retry would then throw. Caller retries.
        retry: "never",
        callback: (token: string) => finish(token),
        "error-callback": () => finish(null),
        "timeout-callback": () => finish(null),
        "expired-callback": () => finish(null),
        "before-interactive-callback": () => {
          window.clearTimeout(timer);
          timer = window.setTimeout(() => finish(null), INTERACTIVE_TIMEOUT_MS);
          setWaiting(true);
        },
        "after-interactive-callback": () => setWaiting(false),
      });
    } catch {
      finish(null);
    }
  });
}
